"use client";
import Link from "next/link";
import { ArrowRight, Shield, Cpu, Globe, Award } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { hero as data } from "@/lib/data/platform-performance";

const trustIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  "15+ Years": Award,
  "300+ Clients": Globe,
  "50+ Platforms": Cpu,
  "Zero Incidents": Shield,
};

export function Hero() {
  return (
    <Section id="top" className="relative overflow-hidden pt-12 pb-16 sm:pt-20 sm:pb-24">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/4 left-1/4 h-96 w-96 rounded-full bg-[var(--color-cyan)] opacity-30 blur-[140px] animate-pulse-glow" />
        <div className="absolute bottom-1/4 right-1/4 h-72 w-72 rounded-full bg-[var(--color-purple)] opacity-25 blur-[120px] animate-pulse-glow" style={{ animationDelay: "2s" }} />
      </div>
      <Container>
        <Reveal>
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-cyan)]" />
              <span className="eyebrow-tiny text-[var(--color-cyan)]">{data.eyebrow}</span>
            </div>
          </div>
        </Reveal>
        <Reveal delay={80}>
          <h1 className="display-hero text-balance">
            {data.title}{" "}
            <span className="bg-gradient-to-br from-[var(--color-cyan)] via-[var(--color-purple)] to-[var(--color-cyan)] bg-clip-text text-transparent">
              {data.titleAccent}
            </span>
          </h1>
        </Reveal>
        <Reveal delay={160}>
          <p className="mt-6 max-w-3xl text-body leading-relaxed text-[rgb(var(--fg-muted))]">
            {data.lede}
          </p>
        </Reveal>
        <Reveal delay={220}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link href={data.ctaPrimary.href} className="btn-primary-gradient h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl shadow-[var(--color-crimson)/40]">
              {data.ctaPrimary.label} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link href={data.ctaSecondary.href} className="btn-glass h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base">
              {data.ctaSecondary.label}
            </Link>
          </div>
        </Reveal>
        <Reveal delay={300}>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-5xl">
            {data.trustIndicators.map((ind) => {
              const Icon = trustIconMap[ind] ?? Award;
              return (
                <div key={ind} className="glass rounded-2xl p-4 flex items-center gap-3">
                  <span
                    className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
                    style={{
                      background: `color-mix(in oklch, var(--color-cyan) 18%, transparent)`,
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-cyan) 35%, transparent)`,
                    }}
                  >
                    <Icon className="h-4 w-4 text-[var(--color-cyan)]" />
                  </span>
                  <span className="text-xs font-semibold leading-snug">{ind}</span>
                </div>
              );
            })}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
